import * as React from 'react';
import { useTheme } from '@emotion/react';
import { motion } from 'motion/react';
import { StoryScene } from '../story';

interface FieldInspectionArtworkProps {
  scene: StoryScene;
  appOffset: number;
  sketch: number;
  live: number;
  ready: number;
  reducedMotion: boolean;
}

export const FieldInspectionArtwork: React.FC<FieldInspectionArtworkProps> = ({
  scene,
  appOffset,
  sketch,
  live,
  ready,
  reducedMotion
}) => {
  const theme = useTheme();
  const colors = theme.featureGraph;
  const webApp = scene >= StoryScene.ORGANIZATION;
  const transition = { duration: reducedMotion ? 0 : 0.7 };
  return (
    <motion.g initial={false} animate={{ opacity: webApp ? 0 : 1 }} transition={transition}>
      <g transform={`translate(${45 + appOffset} 265)`}>
        <rect
          width="220"
          height="350"
          rx="30"
          fill={theme.surface.background}
          stroke={theme.text.heading}
          strokeWidth="2"
          pathLength="1"
          strokeDasharray="1"
          strokeDashoffset={1 - Math.min(1, sketch * 2)}
        />
        <rect
          width="220"
          height="350"
          rx="30"
          fill="none"
          stroke={colors.runtime.accent}
          strokeWidth="2"
          opacity={live}
        />
        <g opacity={1 - live} fill="none" stroke={theme.text.heading} strokeWidth="1.5" strokeLinecap="round">
          {[
            'M20 52 H125 M20 78 H175 M20 100 H110',
            'M20 125 H200 V197 H20 Z M20 197 L85 145 L130 175 L160 150 L200 197',
            'M20 220 H155 M185 211 H200 V226 H185 Z M20 250 H145 M185 241 H200 V256 H185 Z',
            'M20 280 H200 V311 H20 Z M75 296 H145'
          ].map((path, index) => (
            <path
              key={path}
              d={path}
              pathLength="1"
              strokeDasharray="1"
              strokeDashoffset={1 - Math.max(0, Math.min(1, sketch * 4 - index))}
            />
          ))}
        </g>
        <g opacity={live}>
          <rect x="78" y="12" width="64" height="5" rx="3" fill={theme.surface.border} />
          <text x="20" y="52" fill={colors.runtime.accent} fontSize="10" letterSpacing="2">
            FIELD OPERATIONS
          </text>
          <text x="20" y="84" fill={theme.text.heading} fontSize="22" fontWeight="600">
            Site inspection
          </text>
          <text x="20" y="107" fill={theme.text.paragraph} fontSize="11">
            Pump station · Unit 04
          </text>
          <rect x="16" y="125" width="188" height="72" rx="12" fill={theme.card.background} />
          <path
            d="M45 179V145H75V158H105V140H132V167H174V179Z"
            fill={colors.runtime.background}
            stroke={colors.runtime.muted}
            strokeWidth="1.5"
          />
          <circle cx="170" cy="143" r="6" fill={colors.runtime.muted} />
          <text x="20" y="224" fill={theme.text.heading} fontSize="12">
            Equipment checked
          </text>
          <text x="188" y="224" fill={colors.feature.accent} fontSize="16">
            ✓
          </text>
          <text x="20" y="252" fill={theme.text.heading} fontSize="12">
            Photo captured
          </text>
          <text x="188" y="252" fill={colors.feature.accent} fontSize="16">
            ✓
          </text>
          <rect
            x="16"
            y="275"
            width="188"
            height="36"
            rx="9"
            fill={colors.runtime.background}
            stroke={colors.runtime.border}
          />
          <circle cx="34" cy="293" r="3" fill={colors.feature.accent} />
          <text x="46" y="297" fill={theme.text.heading} fontSize="11">
            {scene === StoryScene.APP
              ? ready < 1
                ? 'App built in OXIDE'
                : 'Saved on this device'
              : 'Inspection synchronized'}
          </text>
          <text x="110" y="332" textAnchor="middle" fill={theme.text.paragraph} fontSize="9">
            {scene === StoryScene.APP
              ? ready < 1
                ? 'Your interface. Your logic.'
                : 'No connection? Keep working.'
              : 'Ready for the next workflow.'}
          </text>
        </g>
      </g>
    </motion.g>
  );
};
